(function () {
    if (window.EEGFilterResultsController) return;

    const normalizeUrl = (url) => {
        const nextUrl = new URL(url, window.location.origin);
        return `${nextUrl.pathname}${nextUrl.search}`;
    };

    const shouldAutoApply = () => window.matchMedia('(min-width: 990px)').matches;

    const storageAvailable = (() => {
        try {
            const testKey = 'eegFilterCacheTest';
            sessionStorage.setItem(testKey, '1');
            sessionStorage.removeItem(testKey);
            return true;
        } catch (error) {
            return false;
        }
    })();

    const createStorageCache = (storageKey, limit) => {
        if (!storageKey || !storageAvailable) return null;

        const readEntries = () => {
            try {
                const raw = sessionStorage.getItem(storageKey);
                if (!raw) return [];
                const parsed = JSON.parse(raw);
                if (!Array.isArray(parsed)) return [];
                return parsed.filter((entry) => entry && typeof entry.url === 'string' && typeof entry.html === 'string');
            } catch (error) {
                return [];
            }
        };

        const writeEntries = (entries) => {
            try {
                sessionStorage.setItem(storageKey, JSON.stringify(entries));
            } catch (error) {
                return;
            }
        };

        const hydrate = (cache) => {
            const entries = readEntries();
            if (!entries.length) return;
            const trimmedEntries = entries.slice(-limit);
            trimmedEntries.forEach((entry) => {
                if (!cache.has(entry.url) && cache.size < limit) {
                    cache.set(entry.url, entry.html);
                }
            });
            if (trimmedEntries.length !== entries.length) {
                writeEntries(trimmedEntries);
            }
        };

        const persist = (url, html) => {
            const entries = readEntries().filter((entry) => entry.url !== url);
            entries.push({ url, html });
            while (entries.length > limit) {
                entries.shift();
            }
            writeEntries(entries);
        };

        return { hydrate, persist };
    };

    const create = (config) => {
        if (!config || !config.section) return null;

        const section = config.section;
        const sectionId = config.sectionId || '';
        const sectionSelector = config.sectionSelector || '';
        const historyStateKey = config.historyStateKey || 'pageUrl';
        const cacheLimit = Number(config.cacheLimit) || 12;
        const setLoading = typeof config.setLoading === 'function' ? config.setLoading : () => {};
        const updateSection = typeof config.updateSection === 'function' ? config.updateSection : null;
        const buildFiltersUrl = typeof config.buildFiltersUrl === 'function' ? config.buildFiltersUrl : null;
        const getScrollAnchor = typeof config.getScrollAnchor === 'function' ? config.getScrollAnchor : () => section;
        const onAfterUpdate = typeof config.onAfterUpdate === 'function' ? config.onAfterUpdate : null;
        const closeFilters = typeof config.closeFilters === 'function' ? config.closeFilters : null;
        const storageKey = config.storageKey || '';

        if (!sectionId || !sectionSelector || !updateSection || !buildFiltersUrl) return null;

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const responseCache = new Map();
        const storageCache = createStorageCache(storageKey, cacheLimit);

        if (storageCache) {
            storageCache.hydrate(responseCache);
        }

        let activeUrl = normalizeUrl(window.location.href);
        let activeRequestId = 0;
        let activeController = null;

        const setCache = (url, html) => {
            if (!responseCache.has(url) && responseCache.size >= cacheLimit) {
                const firstKey = responseCache.keys().next().value;
                responseCache.delete(firstKey);
            }
            responseCache.set(url, html);
            if (storageCache) {
                storageCache.persist(url, html);
            }
        };

        const getCachedSection = (url) => {
            if (!responseCache.has(url)) return null;
            const html = responseCache.get(url);
            if (!html) return null;
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return doc.querySelector(sectionSelector);
        };

        const getHeaderOffset = () => {
            const header = document.querySelector('[data-header]');
            if (!header) return 0;
            if (header.classList.contains('is-scrolled')) {
                return header.getBoundingClientRect().height;
            }
            header.classList.add('is-scrolled');
            const height = header.getBoundingClientRect().height;
            header.classList.remove('is-scrolled');
            return height;
        };

        const getAnchorPosition = () => {
            const anchor = getScrollAnchor();
            if (!anchor) return null;
            return anchor.getBoundingClientRect().bottom + window.scrollY;
        };

        const scrollToResults = () => {
            const anchorPosition = getAnchorPosition();
            if (anchorPosition === null) return;
            const headerOffset = getHeaderOffset();
            const nextTop = Math.max(0, anchorPosition - headerOffset);
            if (Math.abs(window.scrollY - nextTop) < 8) return;
            const behavior = prefersReducedMotion ? 'auto' : 'smooth';
            window.scrollTo({ top: nextTop, behavior });
        };

        const scheduleScrollToResults = () => {
            window.requestAnimationFrame(() => {
                scrollToResults();
            });
        };

        const fetchAndRender = async (targetUrl, options = {}) => {
            const { pushState = true, useCache = true, scroll = true } = options;
            const cleanUrl = normalizeUrl(targetUrl);

            if (pushState && cleanUrl === activeUrl) {
                return;
            }

            const requestId = ++activeRequestId;

            if (activeController) {
                activeController.abort();
                activeController = null;
            }

            if (useCache) {
                const cachedSection = getCachedSection(cleanUrl);
                if (cachedSection) {
                    updateSection(cachedSection);
                    if (pushState) {
                        window.history.pushState({ [historyStateKey]: cleanUrl }, '', cleanUrl);
                    }
                    activeUrl = cleanUrl;
                    if (onAfterUpdate) {
                        onAfterUpdate();
                    }
                    if (scroll) {
                        scheduleScrollToResults();
                    }
                    setLoading(false);
                    return;
                }
            }

            activeController = new AbortController();
            setLoading(true);

            const url = new URL(cleanUrl, window.location.origin);
            const fetchUrl = new URL(url.toString());
            fetchUrl.searchParams.set('section_id', sectionId);

            try {
                const response = await fetch(fetchUrl.toString(), {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    signal: activeController.signal,
                });
                if (!response.ok) {
                    if (requestId === activeRequestId) {
                        setLoading(false);
                        activeController = null;
                        window.location.href = url.toString();
                    }
                    return;
                }
                const html = await response.text();
                if (requestId !== activeRequestId) return;

                setCache(cleanUrl, html);
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const newSection = doc.querySelector(sectionSelector);
                if (!newSection) {
                    setLoading(false);
                    activeController = null;
                    window.location.href = url.toString();
                    return;
                }
                if (requestId !== activeRequestId) return;

                updateSection(newSection);
                if (requestId !== activeRequestId) return;
                if (pushState) {
                    window.history.pushState({ [historyStateKey]: cleanUrl }, '', cleanUrl);
                }
                activeUrl = cleanUrl;
                if (onAfterUpdate) {
                    onAfterUpdate();
                }
                if (requestId !== activeRequestId) return;
                if (scroll) {
                    scheduleScrollToResults();
                }
                setLoading(false);
                activeController = null;
            } catch (error) {
                if (error.name === 'AbortError') return;
                if (requestId === activeRequestId) {
                    setLoading(false);
                    activeController = null;
                    window.location.href = url.toString();
                }
            }
        };

        const applyFilters = (options = {}) => {
            const { closeOnComplete = false } = options;
            const targetUrl = buildFiltersUrl();
            const normalizedTarget = normalizeUrl(targetUrl);
            if (normalizedTarget === activeUrl) {
                if (closeOnComplete && closeFilters) {
                    closeFilters();
                }
                return;
            }
            fetchAndRender(targetUrl, { pushState: true, scroll: true });
            if (closeOnComplete && closeFilters) {
                closeFilters();
            }
        };

        return {
            fetchAndRender,
            applyFilters,
            normalizeUrl,
            shouldAutoApply,
        };
    };

    const bindFilterUI = (config) => {
        if (!config || !config.section) return null;

        const section = config.section;
        const openFilters = typeof config.openFilters === 'function' ? config.openFilters : () => {};
        const closeFilters = typeof config.closeFilters === 'function' ? config.closeFilters : () => {};
        const applyFilters = typeof config.applyFilters === 'function' ? config.applyFilters : () => {};
        const fetchAndRender = typeof config.fetchAndRender === 'function' ? config.fetchAndRender : null;
        const shouldAutoApply = typeof config.shouldAutoApply === 'function' ? config.shouldAutoApply : () => false;
        const handleCustomClick = typeof config.handleCustomClick === 'function' ? config.handleCustomClick : null;
        const handleCustomChange = typeof config.handleCustomChange === 'function' ? config.handleCustomChange : null;

        const handleClick = (event) => {
            const target = event.target instanceof Element ? event.target : null;
            if (!target) return;

            const filterToggle = target.closest('[data-filter-toggle]');
            if (filterToggle) {
                openFilters();
                return;
            }

            const filterClose = target.closest('[data-filter-close]');
            const filterOverlay = target.closest('[data-filter-overlay]');
            if (filterClose || filterOverlay) {
                closeFilters();
                return;
            }

            const groupToggle = target.closest('[data-filter-group-toggle]');
            if (groupToggle) {
                const group = groupToggle.closest('[data-filter-group]');
                if (!group) return;
                const isOpen = group.classList.contains('is-open');
                group.classList.toggle('is-open', !isOpen);
                groupToggle.setAttribute('aria-expanded', String(!isOpen));
                return;
            }

            const categoryToggle = target.closest('[data-category-toggle]');
            if (categoryToggle) {
                event.preventDefault();
                const item = categoryToggle.closest('[data-category-item]');
                if (!item) return;
                const isOpen = item.classList.contains('is-open');
                const items = section.querySelectorAll('[data-category-item].is-open');
                items.forEach((openItem) => {
                    if (openItem !== item) {
                        openItem.classList.remove('is-open');
                        const openToggle = openItem.querySelector('[data-category-toggle]');
                        if (openToggle) openToggle.setAttribute('aria-expanded', 'false');
                    }
                });
                item.classList.toggle('is-open', !isOpen);
                categoryToggle.setAttribute('aria-expanded', String(!isOpen));
                return;
            }

            const applyButton = target.closest('[data-apply-filters]');
            if (applyButton) {
                event.preventDefault();
                applyFilters({ closeOnComplete: true });
                return;
            }

            const paginationLink = target.closest('.pagination a');
            if (paginationLink && paginationLink.href && fetchAndRender) {
                event.preventDefault();
                fetchAndRender(paginationLink.href, { pushState: true, scroll: true });
                return;
            }

            if (handleCustomClick && handleCustomClick(event, target)) {
                return;
            }
        };

        const handleChange = (event) => {
            const target = event.target;
            if (!(target instanceof HTMLElement)) return;

            if (handleCustomChange && handleCustomChange(event, target)) {
                return;
            }

            if (target.matches('[data-sort-select]')) {
                applyFilters();
                return;
            }

            if (target.matches('input[type="radio"][data-price-range]')) {
                if (shouldAutoApply()) {
                    applyFilters();
                }
                return;
            }

            if (target.matches('input[type="checkbox"]:not([data-price-range])')) {
                if (shouldAutoApply()) {
                    applyFilters();
                }
            }
        };

        section.addEventListener('click', handleClick);
        section.addEventListener('change', handleChange);

        return {
            destroy: () => {
                section.removeEventListener('click', handleClick);
                section.removeEventListener('change', handleChange);
            },
        };
    };

    window.EEGFilterResultsController = {
        create,
        normalizeUrl,
        shouldAutoApply,
        bindFilterUI,
    };
})();
